import React, { useState, useEffect, useCallback } from 'react';
import { LayoutGrid, Plus, Edit, Trash2, ChevronUp, ChevronDown, Save, Eye, EyeOff } from 'lucide-react';
import api from '../../utils/api';
import Modal from '../../components/common/Modal';
import { useToast } from '../../components/common/Toast';
import './Modules.css';

const EMPTY_MODULE = {
  name: '',
  description: '',
  icon: 'Link',
  module_type: 'external',
  url: '',
  image_url: '',
  is_enabled: true,
  visible_to_roles: ['admin', 'manager', 'staff'],
};

function Modules() {
  const toast = useToast();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_MODULE });
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModule, setDeleteModule] = useState(null);

  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/modules');
      setModules(res.modules || []);
    } catch (err) {
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const handleAddClick = () => {
    setEditingModule(null);
    setForm({ ...EMPTY_MODULE });
    setFormOpen(true);
  };

  const handleEditClick = (mod) => {
    setEditingModule(mod);
    setForm({
      name: mod.name || '',
      description: mod.description || '',
      icon: mod.icon || 'Link',
      module_type: mod.module_type || 'external',
      url: mod.url || '',
      image_url: mod.image_url || '',
      is_enabled: mod.is_enabled,
      visible_to_roles: mod.visible_to_roles || ['admin', 'manager', 'staff'],
    });
    setFormOpen(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role) => {
    setForm(prev => {
      const roles = prev.visible_to_roles || [];
      if (roles.includes(role)) {
        return { ...prev, visible_to_roles: roles.filter(r => r !== role) };
      }
      return { ...prev, visible_to_roles: [...roles, role] };
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Module name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingModule) {
        await api.put(`/admin/modules/${editingModule.id}`, form);
        toast.success('Module updated');
      } else {
        await api.post('/admin/modules', form);
        toast.success('Module created');
      }
      setFormOpen(false);
      loadModules();
    } catch (err) {
      toast.error(err.message || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (mod) => {
    try {
      await api.put(`/admin/modules/${mod.id}`, { is_enabled: !mod.is_enabled });
      toast.success(`${mod.name} ${mod.is_enabled ? 'disabled' : 'enabled'}`);
      loadModules();
    } catch (err) {
      toast.error('Failed to toggle module');
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newModules = [...modules];
    [newModules[index - 1], newModules[index]] = [newModules[index], newModules[index - 1]];
    const order = newModules.map((m, i) => ({ id: m.id, sort_order: i }));

    try {
      await api.put('/admin/modules/reorder', { order });
      setModules(newModules);
    } catch (err) {
      toast.error('Failed to reorder modules');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === modules.length - 1) return;
    const newModules = [...modules];
    [newModules[index], newModules[index + 1]] = [newModules[index + 1], newModules[index]];
    const order = newModules.map((m, i) => ({ id: m.id, sort_order: i }));

    try {
      await api.put('/admin/modules/reorder', { order });
      setModules(newModules);
    } catch (err) {
      toast.error('Failed to reorder modules');
    }
  };

  const handleDeleteClick = (mod) => {
    if (mod.builtin_key) {
      toast.error('Built-in modules cannot be deleted. Use the toggle to disable.');
      return;
    }
    setDeleteModule(mod);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModule) return;
    try {
      await api.delete(`/admin/modules/${deleteModule.id}`);
      toast.success(`${deleteModule.name} deleted`);
      setDeleteModalOpen(false);
      setDeleteModule(null);
      loadModules();
    } catch (err) {
      toast.error(err.message || 'Failed to delete module');
    }
  };

  return (
    <div className="admin-modules">
      <div className="page-header">
        <LayoutGrid size={22} strokeWidth={2} />
        <h1>Hub Modules</h1>
        <button className="btn-primary" onClick={handleAddClick}>
          <Plus size={16} />
          Add External Link
        </button>
      </div>

      <div className="modules-list-card">
        {loading ? (
          <p className="empty-text">Loading modules...</p>
        ) : modules.length === 0 ? (
          <p className="empty-text">No modules configured.</p>
        ) : (
          <div className="modules-list">
            {modules.map((mod, index) => (
              <div
                key={mod.id}
                className={`module-item ${!mod.is_enabled ? 'module-disabled' : ''}`}
              >
                <div className="module-reorder">
                  <button className="reorder-btn" onClick={() => handleMoveUp(index)} disabled={index === 0}>
                    <ChevronUp size={14} />
                  </button>
                  <button className="reorder-btn" onClick={() => handleMoveDown(index)} disabled={index === modules.length - 1}>
                    <ChevronDown size={14} />
                  </button>
                </div>
                <div className="module-info">
                  <div className="module-name-row">
                    <span className="module-name">{mod.name}</span>
                    <span className={`module-type-badge ${mod.module_type}`}>
                      {mod.module_type}
                    </span>
                    {mod.builtin_key && (
                      <span className="module-builtin-badge">built-in</span>
                    )}
                  </div>
                  {mod.description && (
                    <p className="module-description">{mod.description}</p>
                  )}
                  {mod.url && (
                    <span className="module-url">{mod.url}</span>
                  )}
                  <div className="module-roles">
                    {(mod.visible_to_roles || []).map(role => (
                      <span key={role} className="role-tag">{role}</span>
                    ))}
                  </div>
                </div>
                <div className="module-actions">
                  <button
                    className="icon-btn"
                    onClick={() => handleToggleEnabled(mod)}
                    title={mod.is_enabled ? 'Disable' : 'Enable'}
                  >
                    {mod.is_enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button className="icon-btn" onClick={() => handleEditClick(mod)} title="Edit">
                    <Edit size={14} />
                  </button>
                  {!mod.builtin_key && (
                    <button className="icon-btn icon-btn-danger" onClick={() => handleDeleteClick(mod)} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Module Form Modal */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingModule ? 'Edit Module' : 'Add External Link Module'}
        size="medium"
      >
        <form onSubmit={handleFormSubmit} className="module-form">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="Module name"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Brief description"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Icon (Lucide name)</label>
              <input
                type="text"
                value={form.icon}
                onChange={(e) => handleFormChange('icon', e.target.value)}
                placeholder="e.g., Link, Receipt, Clipboard"
              />
            </div>
            <div className="form-group">
              <label>URL</label>
              <input
                type="url"
                value={form.url}
                onChange={(e) => handleFormChange('url', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="form-group">
            <label>Image URL (optional)</label>
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => handleFormChange('image_url', e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label>Visible to Roles</label>
            <div className="role-checkboxes">
              {['admin', 'manager', 'staff'].map(role => (
                <label key={role} className="role-checkbox">
                  <input
                    type="checkbox"
                    checked={(form.visible_to_roles || []).includes(role)}
                    onChange={() => handleRoleToggle(role)}
                  />
                  <span>{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => setFormOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              <Save size={14} />
              {saving ? 'Saving...' : 'Save Module'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteModule(null); }}
        title="Delete Module"
        size="small"
      >
        <p style={{ marginBottom: '16px', color: '#374151' }}>
          Are you sure you want to delete <strong>{deleteModule?.name}</strong>? This cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={() => { setDeleteModalOpen(false); setDeleteModule(null); }}>Cancel</button>
          <button className="btn-danger" onClick={handleDeleteConfirm}>Delete</button>
        </div>
      </Modal>
    </div>
  );
}

export default Modules;
